<script setup lang="ts">
/**
 * The catch-all for a URL none of this app's hand-rolled routes recognise —
 * see `state/not-found.ts` for what "recognise" means here and why nothing
 * before this screen ever needed to say "wrong" out loud.
 *
 * A plain link back to `/` rather than a button that calls into any of the
 * app's own navigation: this screen renders instead of everything else in
 * `App.vue`'s `phase`, precisely because the URL didn't match anything, so
 * there is no session state, no scene and no history to hand back into —
 * a full navigation is the only honest way out of it.
 */
</script>

<template>
  <div class="not-found">
    <div class="card">
      <p class="code">404</p>
      <h1>Page not found</h1>
      <p class="hint">There's nothing at this address.</p>
      <a class="back" href="/">Back to Home</a>
    </div>
  </div>
</template>

<style scoped>
.not-found {
  display: grid;
  place-items: center;
  height: 100%;
  padding: 2rem;
  background: var(--steel-900);
}

.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 2rem 2.5rem 2.25rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.5rem;
  background: var(--steel-800);
  text-align: center;
}

.code {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(3.5rem, 12vw, 5.5rem);
  line-height: 1;
  color: var(--accent);
  text-shadow: 0 0 32px color-mix(in srgb, var(--accent) 45%, transparent);
}

h1 {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  color: var(--steel-200);
}

.hint {
  margin: 0;
  color: var(--steel-400);
  font-size: 0.9rem;
}

.back {
  margin-top: 1rem;
  padding: 0.6rem 1.6rem;
  border: 1px solid var(--accent-dim);
  border-radius: 0.3rem;
  background: var(--accent);
  color: var(--steel-900);
  font-family: var(--font-body);
  font-weight: 600;
  text-decoration: none;
  transition:
    background 140ms ease,
    border-color 140ms ease;
}

.back:hover {
  background: var(--accent-dim);
  border-color: var(--accent);
}
</style>
